import React, { useEffect, useState, useRef, useCallback, useContext, useMemo } from "react";
import { useNavigate } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import Map, { Source, Layer } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import wkt from "wellknown";
import bbox from "@turf/bbox";
import center from "@turf/center";
import area from "@turf/area";
import { useParams } from "react-router-dom";
import { client } from "../services/api";
import { toast } from "react-toastify";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import { LinearProgressWithLabel } from "../components/LinearProgressWithLabel";
import { getLabelByValue, structureTypes, dataTypes, mainObjectives } from "../constants/formOptions";
import LoadingModal from "../components/LoadingModal";
import ModalRequestAssessment from "../components/ModalRequestAssessment";
import ModalApproveAssessment from "../components/ModalApproveAssessment";
import ModalAddStrucure from "../components/ModalAddStrucure";
import ModalIssue from "../components/ModalIssue";
import ModalAddDataFile from "../components/ModalAddDataFile";
import ModalAddDataURL from "../components/ModalAddDataURL";

function RequestsDetail() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState(false);
  const { user } = useContext(AuthContext);
  const [request, setRequest] = useState({});
  const [geometryInfo, setGeometryInfo] = useState({ center: null, area: null });
  const mapRef = useRef();
  const [selectedStructure, setSelectedStructure] = useState(null);
  const [selectedDataFile, setSelectedDataFile] = useState(null);
  const [selectedDataURL, setSelectedDataURL] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);
  const [selectedIssue, setSelectedIssue] = useState(null);

  // modals
  const [isAssessmentModalOpen, setIsAssessmentModalOpen] = useState(false);
  const [isApproveAssessmentModalOpen, setIsApproveAssessmentModalOpen] = useState(false);
  const [isStructureModalOpen, setStructureModalOpen] = useState(false);
  const [isIssuesModalOpen, setIssueModalOpen] = useState(false);
  const [isDataFileModalOpen, setDataFileModalOpen] = useState(false);
  const [isDataURLModalOpen, setDataURLModalOpen] = useState(false);

  const [viewport, setViewport] = useState({
    latitude: 41.8719,
    longitude: 12.56,
    zoom: 10,
  });

  const geoJsonLayer = {
    id: 'geojson-layer',
    type: 'fill',
    paint: {
      'fill-color': '#010E2D',
      'fill-opacity': 0.5,
    },
  };

  const moveTimeout = useRef();

  const onMapMove = useCallback((evt) => {
    clearTimeout(moveTimeout.current);
    moveTimeout.current = setTimeout(() => {
      setViewport(evt.viewState);
    }, 150);
  }, []);

  const fitGeometry = useCallback(() => {
    if (request.form?.areaOfInterest && mapRef.current) {
      const bounds = bbox(request.form?.areaOfInterest);

      mapRef.current.fitBounds(bounds, { padding: 20 });
    }
  }, [request.form?.areaOfInterest]);

  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.resize();
    }
    fitGeometry();
  }, [request.form?.areaOfInterest, fitGeometry]);

  useEffect(() => {
    client.getRequestById(requestId)
      .then((resp) => setRequest(resp.data))
      .catch((err) => console.error(err.message));
  }, [requestId]);

  useEffect(() => {
    if (request.form) {
      const geoCenter = center(request.form.areaOfInterest);
      const [lng, lat] = geoCenter.geometry.coordinates;

      const geoArea = area(request.form.areaOfInterest) / 1_000_000;

      setGeometryInfo({ center: { lat, lng }, area: geoArea.toFixed(2) });
    }
  }, [request]);

  const formGeohazards = useMemo(() => {
    const geohazards = request.form?.geohazards;
    return Array.isArray(geohazards) ? geohazards : [];
  }, [request.form?.geohazards]);

  const geohazardLayers = useMemo(() => {
    if (!formGeohazards) return [];
    return formGeohazards.map((g) => {
      const layerId = `layer-${g.geohazardType}-${g._id}`;
      return (
        <Source key={layerId} id={layerId} type="geojson" data={{ type: "FeatureCollection", features: g.geometry.features }}>
          <Layer
            id={layerId}
            type="fill"
            paint={{
              'fill-opacity': 0.5,
              'fill-color': g.color,
            }}
          />
        </Source>
      );
    });
  }, [formGeohazards]);

  const geohazardBounds = useMemo(() => {
    if (!formGeohazards || formGeohazards.length === 0) return null;

    const allFeatures = formGeohazards.flatMap(g => g?.geometry?.features || []);
    const featureCollection = {
      type: "FeatureCollection",
      features: allFeatures,
    };

    return bbox(featureCollection);
  }, [formGeohazards]);

  const geohazardMapRef = useRef();

  useEffect(() => {
    if (geohazardMapRef.current && geohazardBounds) {
      geohazardMapRef.current.fitBounds(geohazardBounds, { padding: 20 });
    }
  }, [geohazardBounds]);

  const structureBounds = useMemo(() => {
    const structures = request.form?.structures || [];

    const extentGeoms = structures
      .map(s => s.layer?.extent)
      .filter(Boolean)
      .map(extentWKT => wkt.parse(extentWKT));

    if (extentGeoms.length === 0) return null;

    const featureCollection = {
      type: "FeatureCollection",
      features: extentGeoms.map(geom => ({
        type: "Feature",
        geometry: geom,
      })),
    };

    return bbox(featureCollection);
  }, [request.form?.structures]);

  const structureMapRef = useRef();

  useEffect(() => {
    if (structureMapRef.current && structureBounds) {
      structureMapRef.current.fitBounds(structureBounds, { padding: 20 });
    }
  }, [structureBounds]);

  const dataFilesBounds = useMemo(() => {
    const dataFiles = request.form?.dataFiles || [];

    const extentGeoms = dataFiles
      .map(s => s.layer?.extent)
      .filter(Boolean)
      .map(extentWKT => wkt.parse(extentWKT));

    if (extentGeoms.length === 0) return null;

    const featureCollection = {
      type: "FeatureCollection",
      features: extentGeoms.map(geom => ({
        type: "Feature",
        geometry: geom,
      })),
    };

    return bbox(featureCollection);
  }, [request.form?.dataFiles]);

  const dataFileMapRef = useRef();

  useEffect(() => {
    if (dataFileMapRef.current && dataFilesBounds) {
      dataFileMapRef.current.fitBounds(dataFilesBounds, { padding: 20 });
    }
  }, [dataFilesBounds]);

  const removeStructure = async (structure) => {
    if (window.confirm("The structure will be deleted, continue?")) {
      setLoading(true);
      const result = await client.deleteRequestStructure(request._id, structure._id);
      if (result.statusText === 'OK') {
        toast.success("Structure file deleted successfully!");

        const updatedStructures = (request.form?.structures || []).filter((s) => s._id !== structure._id);
        setRequest((prevRequest) => ({
          ...prevRequest,
          form: {
            ...prevRequest.form,
            structures: updatedStructures,
          },
        }));
      } else {
        toast.error("Structure file deletion failed!");
      }
      setLoading(false);
    }
  };

  const removeDataFile = async (dataFile) => {
    if (window.confirm("The dataFile will be deleted, continue?")) {
      setLoading(true);
      const result = await client.deleteRequestDataFile(request._id, dataFile._id);

      if (result.statusText === 'OK') {
        toast.success("DataFile deleted successfully!");

        const updatedDataFiles = (request.form?.dataFiles || []).filter((d) => d._id !== dataFile._id);
        setRequest((prevRequest) => ({
          ...prevRequest,
          form: {
            ...prevRequest.form,
            dataFiles: updatedDataFiles,
          },
        }));
      } else {
        toast.error("DataFile deletion failed!");
      }
      setLoading(false);
    }
  }

  const removeDataURL = async (dataURL) => {
    if (window.confirm("The dataURL will be deleted, continue?")) {
      setLoading(true);
      const result = await client.deleteRequestDataURL(request._id, dataURL._id);
      if (result.statusText === 'OK') {
        toast.success("DataURL deleted successfully!");

        const updatedDataURLs = (request.form?.dataURLs || []).filter((d) => d._id !== dataURL._id);
        setRequest((prevRequest) => ({
          ...prevRequest,
          form: {
            ...prevRequest.form,
            dataURLs: updatedDataURLs,
          },
        }));
      } else {
        toast.error("DataURL deletion failed!");
      }
      setLoading(false);
    }
  }

  const deleteRequest = async () => {
    if (window.confirm("The request will be deleted, continue?")) {
      setLoading(true);
      const result = await client.deleteRequest(request._id);
      if (result.statusText === 'OK') {
        navigate('/')
      } else {
        toast.error("Request deletion failed!");
      }
      setLoading(false);
    }
  }

  const approveAction = async (action, approvedAssessments) => {
    setLoading(true);
    const response = await client.updateRequestAction(request._id, action._id, {
      status: "approved",
      approvedAssessments,
    });
    if (response.status === 200) {
      const updatedActions = (request.actions || []).map((a) =>
        a._id === action._id ? { ...a, status: 'approved', approvedAssessments } : a
      );
      setRequest((prevRequest) => ({
        ...prevRequest,
        actions: updatedActions,
      }));
    } else {
      toast.error("Failed to approve the action.");
    }
    setLoading(false);
  }

  const rejectAction = async (action, rejectionReason) => {
    if (rejectionReason.trim()) {
      setLoading(true);
      const response = await client.updateRequestAction(request._id, action._id, {
        status: "rejected",
        rejectionReason,
      });
      if (response.status === 200) {
        const updatedActions = (request.actions || []).map((a) =>
          a._id === action._id ? { ...a, status: 'rejected', rejectionReason } : a
        );
        setRequest((prevRequest) => ({
          ...prevRequest,
          actions: updatedActions,
        }));
      } else {
        toast.error("Failed to reject the action.");
      }
    } else {
      toast.error("Please provide a rejection reason.");
    }
    setLoading(false);
  }

  const handleSubmitAssessment = async (action) => {
    setLoading(true);
    const response = await client.addRequestAction(request._id, action);
    if (response.status === 201) {
      setRequest((prevRequest) => ({
        ...prevRequest,
        actions: response.data.actions,
      }));
    }
    setLoading(false);
  };

  const handleSubmitApproveAssessment = async (submission) => {
    submission.accepted ? approveAction(submission.action, submission.approvedAssessments) : rejectAction(submission.action, submission.description);
  };

  return (
    <div className="request-details">
      <Header />
      <div className="main-wrapper">
        <Sidebar />
        <div className="main-content">
          <LoadingModal isOpen={!!isLoading} />
          <ModalRequestAssessment request={request} isOpen={isAssessmentModalOpen} onClose={() => setIsAssessmentModalOpen(false)} onDone={handleSubmitAssessment} />
          <ModalApproveAssessment action={selectedAction} isOpen={isApproveAssessmentModalOpen} onClose={() => setIsApproveAssessmentModalOpen(false)} onDone={handleSubmitApproveAssessment} />
          <ModalAddStrucure isOpen={isStructureModalOpen} onClose={() => setStructureModalOpen(false)} structure={selectedStructure} />
          <ModalIssue isOpen={isIssuesModalOpen} onClose={() => setIssueModalOpen(false)} issue={selectedIssue} />
          <ModalAddDataFile isOpen={isDataFileModalOpen} onClose={() => setDataFileModalOpen(false)} dataFile={selectedDataFile} />
          <ModalAddDataURL isOpen={isDataURLModalOpen} onClose={() => setDataURLModalOpen(false)} dataURL={selectedDataURL} />
          <div className="main-content-primary" style={{ padding: '36px 24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', float: 'right' }}>
              <button onClick={() => window.open("mailto:info@titan4.it", "_blank")} className="button-link" style={{ fontSize: '20px' }}>
                <i className='icon-help'></i>
              </button>
              <a style={{ fontSize: '20px', rotate: '180deg' }}
                onClick={(event) => event.stopPropagation()} href={process.env.REACT_APP_URL_API + `/request/${request._id}/getReport`}
                target="_blank"
                rel="noopener noreferrer"
                download><i className="icon-upload"></i>
              </a>
            </div>
            <h5>Request {request.form?.name || request._id}</h5>
            <p><b style={{ color: '#808080' }}>{request.project?.name}</b></p>
            <div style={{ display: "flex" }}>
              <div style={{ flex: 50 }}>
                <h6>Creation date: {new Date(request.createDate).toLocaleDateString()}</h6>
                <h6>Created by: {request.user?.email}</h6>
                <h6>Activity Scope: {Array.isArray(request.form?.requestType) ? request.form.requestType.join(", ") : ''}</h6>
              </div>
              <div className="content-wrapper" style={{ flex: 50 }}>
                <p>{request.form?.description}</p>
              </div>
            </div>
            <div className="content-wrapper" style={{ flexDirection: 'row', gap: '50px', alignItems: 'center' }}>
              <b>Request Status: {request.status}</b>
              <LinearProgressWithLabel style={{ flexGrow: '1' }} value={Number(request.progress) || 10} />
              <button onClick={() => deleteRequest()} className="outline" style={{ color: '#f0941d', border: 'none', background: 'none' }}>
                <i style={{ color: '#f0941d' }} className='icon-remove'></i> Delete this request
              </button>
            </div>
            {user.admin && (
              <>
                <p>Add the proposal for the preassessment here</p>
                <div className="content-wrapper">
                  <button style={{ maxWidth: '300px' }} onClick={() => setIsAssessmentModalOpen(true)}>+ Add the proposal</button>
                </div>
              </>
            )}
            {request.actions && request.actions.length > 0 && (
              <>
                <h6>Proposals</h6>
                {request.actions.map((action, index) => (
                  <div key={index} className={`content-wrapper action-box ${action.status}`} style={{ cursor: 'pointer' }} onClick={() => { setSelectedAction(action); setIsApproveAssessmentModalOpen(true) }}>
                    <div style={{ display: "flex", justifyContent: 'space-between' }}>
                      <p>Preassessment Proposal {index + 1}</p>
                      <p>{new Date(action.createDate).toLocaleDateString()}</p>
                      <p>
                        {action.status === 'waiting' && <span style={{ color: '#F0941D' }}>Awaiting Action</span>}
                        {action.status === 'approved' && <span style={{ color: '#00BA29' }}>Confirmed</span>}
                        {action.status === 'rejected' && <span style={{ color: '#E10101' }}>Turned Down</span>}
                      </p>
                    </div>
                  </div>
                ))}
              </>
            )}
            <h6>Request details</h6>
            <div className="content-wrapper" style={{ marginBottom: '36px' }}>
              <div className="row-flex">
                <div>
                  <div style={{ flex: 50 }}>
                    <h6>Area of Interest</h6>
                    <span>Area: {geometryInfo.area && `${geometryInfo.area} km²`}</span><br />
                    <span>Geographical Coordinates: {geometryInfo.center?.lat && `${geometryInfo.center?.lat?.toFixed(5)}, ${geometryInfo.center?.lng?.toFixed(5)}`}</span>
                  </div>
                  <div style={{ flex: 50 }}>
                    <h6>Time of Interest</h6>
                    {request.form?.timeOfInterests && Array.isArray(request.form.timeOfInterests) && request.form.timeOfInterests.map((i) =>
                      <React.Fragment key={i.id}>
                        <span>Type: {i.monitoring ? 'Monitoring' : 'Assessment'}</span><br />
                        <span>Duration: From {`${i.from || "Not specified"} to ${i.to || "Not specified"}`}</span><br />
                      </React.Fragment>
                    )}
                  </div>
                </div>
                <div className="map-wrapper" style={{ margin: 0 }}>
                  <Map
                    reuseMaps
                    ref={mapRef}
                    className="area-map"
                    mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
                    initialViewState={viewport}
                    mapStyle="mapbox://styles/mapbox/streets-v11"
                    onMove={onMapMove}
                  >
                    {request.form?.areaOfInterest && (
                      <Source id="geojson-source" type="geojson" data={request.form?.areaOfInterest}>
                        <Layer {...geoJsonLayer} />
                      </Source>
                    )}
                  </Map>
                </div>
              </div>
            </div>
            <div className="content-wrapper" style={{ marginBottom: '36px' }}>
              <div className="row-flex">
                <div>
                  <h6>Main objective</h6>
                  <span>{Array.isArray(request.form?.selectedObjectives) ? request.form.selectedObjectives.map((i) => getLabelByValue(mainObjectives, i)).join(', ') : ''}</span>
                  <ul className="item-list flex-list">
                    {(request.form?.structures || []).map((item, index) => (
                      <li key={index} onClick={() => { setSelectedStructure(item); setStructureModalOpen(true); }}>
                        {item.name} <span>{getLabelByValue(structureTypes, item.type)}</span>
                        <i style={{ float: 'right' }} className='icon-remove' onClick={(e) => { e.stopPropagation(); removeStructure(item); }}></i>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="map-wrapper" style={{ margin: 0 }}>
                  <Map
                    reuseMaps
                    ref={structureMapRef}
                    className="area-map"
                    mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
                    initialViewState={viewport}
                    mapStyle="mapbox://styles/mapbox/streets-v11"
                    onMove={onMapMove}
                  >
                    {(request.form?.structures || []).map(s => {
                      const layerId = `layer-${s.type}-${s._id}`;
                      return <Source key={s._id} type="vector" url={`mapbox://${s.layer?.source_id}`}>
                        <Layer {...{
                          id: layerId,
                          'source-layer': s.layer?.source_layer,
                          type: 'fill',
                          paint: {
                            'fill-color': '#010E2D',
                            'fill-opacity': 0.5,
                          },
                        }} />
                      </Source>;
                    })}
                  </Map>
                </div>
              </div>
            </div>
            <div className="content-wrapper" style={{ marginBottom: '36px' }}>
              <div className="row-flex">
                <div>
                  <h6>Existing Issue</h6>
                  <ul className="item-list flex-list">
                    {formGeohazards.map((item, index) => (
                      <li key={index} onClick={() => { setSelectedIssue(item); setIssueModalOpen(true); }}>
                        <div className="color-item" style={{ backgroundColor: item.color }} /> {item.description} <i style={{ float: 'right' }} className='icon-remove' onClick={() => null}></i>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="map-wrapper" style={{ margin: 0 }}>
                  <Map
                    reuseMaps
                    ref={geohazardMapRef}
                    className="area-map"
                    mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
                    initialViewState={viewport}
                    mapStyle="mapbox://styles/mapbox/streets-v11"
                    onMove={onMapMove}
                  >
                    {geohazardLayers}
                  </Map>
                </div>
              </div>
            </div>
            <div className="content-wrapper" style={{ marginBottom: '66px' }}>
              <div className="row-flex">
                <div>
                  <h6>Existing Data</h6>
                  <ul className="item-list flex-list">
                    {(request.form?.dataFiles || []).map((item, index) => (
                      <li key={index} onClick={() => { setSelectedDataFile(item); setDataFileModalOpen(true); }}>
                        {item.name} <span>{getLabelByValue(dataTypes, item.type)}</span>
                        <i style={{ float: 'right' }} className='icon-remove' onClick={(e) => { e.stopPropagation(); removeDataFile(item); }}></i>
                      </li>
                    ))}
                  </ul>
                  <p>Integrating with Open Data and public sources? {request.form?.openDataIntegration}</p>
                  {request.form?.openDataIntegration === 'yes' && (
                    <ul>
                      {(request.form?.openDataSources || []).map((ds, i) => <li key={i}>{ds}</li>)}
                    </ul>
                  )}
                  {request.form?.dataURLs && request.form.dataURLs.length > 0 && (
                    <>
                      <p>Third Party URLs</p>
                      <ul className="item-list flex-list">
                        {request.form.dataURLs.map((item, index) => (
                          <li key={index} onClick={() => { setSelectedDataURL(item); setDataURLModalOpen(true); }}>
                            {item.name}
                            <i style={{ float: 'right' }} className='icon-remove' onClick={(e) => { e.stopPropagation(); removeDataURL(item); }}></i>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
                <div className="map-wrapper" style={{ margin: 0 }}>
                  <Map
                    reuseMaps
                    ref={dataFileMapRef}
                    className="area-map"
                    mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
                    initialViewState={viewport}
                    mapStyle="mapbox://styles/mapbox/streets-v11"
                    onMove={onMapMove}
                  >
                    {(request.form?.dataFiles || []).map(d => {
                      const layerId = `layer-${d.type}-${d._id}`;
                      if (!d.layer) {
                        console.warn(`DataFile ${d._id} not rendered, no layer associated`);
                        return null;
                      }
                      return <Source key={d._id} type="vector" url={`mapbox://${d.layer?.source_id}`}>
                        <Layer {...{
                          id: layerId,
                          'source-layer': d.layer?.source_layer,
                          type: 'fill',
                          paint: {
                            'fill-color': '#010E2D',
                            'fill-opacity': 0.5,
                          },
                        }} />
                      </Source>;
                    })}
                  </Map>
                </div>
              </div>
            </div>
            <div>&nbsp;</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RequestsDetail;
